<template>
  <div class="qr-reader-container">
    <div id="qr-reader"></div>
    <div class="qr-focus-window">
      <div class="qr-instruction" v-if="instruction">
        {{ instruction }}
      </div>
      <div class="laser-line" v-if="scannerActive"></div>

      <!-- Corner markers -->
      <div class="corner top-left"></div>
      <div class="corner top-right"></div>
      <div class="corner bottom-left"></div>
      <div class="corner bottom-right"></div>
    </div>
    <p v-if="scanResult" class="scan-result">{{ scanResult }}</p>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue';
import { Html5Qrcode, Html5QrcodeScannerState, Html5QrcodeSupportedFormats } from 'html5-qrcode';

defineProps({
  contact: Object,
  eventEdition: Object,
});

const emit = defineEmits(['scanStarted', 'scanStopped', 'scanSuccess', 'scannerError']);

const scannerActive = ref(false);
const scanResult = ref('');
const instruction = ref('Click start scanning to open the camera.');
let html5QrCode = null;

const startScanner = async () => {
  scanResult.value = '';
  instruction.value = 'Requesting camera access...';

  const qrElement = document.getElementById('qr-reader');
  if (!qrElement) {
    emit('scannerError', 'QR scanner area not found in the DOM.');
    emit('scanStopped');
    return;
  }

  if (!html5QrCode) {
    html5QrCode = new Html5Qrcode('qr-reader');
  }

  const state = await html5QrCode.getState?.();
  if (state === Html5QrcodeScannerState.SCANNING) {
    emit('scannerError', 'Scanner is already running.');
    emit('scanStopped');
    return;
  }

  try {
    await html5QrCode.start(
      { facingMode: 'environment' },
      {
        fps: 10,
        aspectRatio: 4 / 3,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      },
      (decodedText) => {
        scanResult.value = decodedText;
        scannerActive.value = false;
        html5QrCode.stop();
        emit('scanSuccess', decodedText);
        emit('scanStopped');
      },
    );
    scannerActive.value = true;
    instruction.value = null;
    emit('scanStarted');
  } catch (err) {
    emit('scannerError', 'Camera access denied or unavailable.');
    scannerActive.value = false;
    emit('scanStopped');
  }
};

const stopScanner = async () => {
  if (html5QrCode && html5QrCode.getState?.() === Html5QrcodeScannerState.SCANNING) {
    await html5QrCode.stop();
    await html5QrCode.clear();
  }
  scannerActive.value = false;
  emit('scanStopped');
};

defineExpose({
  startScanner,
  stopScanner,
});

onBeforeUnmount(() => {
  stopScanner();
});
</script>

<style scoped>
.qr-reader-container {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
}

#qr-reader {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Custom focus box */
.qr-focus-window {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 60%;
  height: 60%;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  overflow: hidden;
}

/* Laser line animation */
.laser-line {
  opacity: 0;
  position: absolute;
  top: -2rem;
  left: 50%;
  width: 140%;
  height: 0px;
  transform: translateX(-50%) rotate(0deg);
  transform-origin: center center;
  background: linear-gradient(
    to right,
    transparent,
    var(--laser-color, var(--brand-primary-color)),
    transparent
  );
  box-shadow:
    0 0 4px 4px var(--laser-color, var(--brand-primary-color)),
    0 0 8px 8px var(--laser-color, var(--brand-primary-color));
  animation:
    fadeIn 0.3s ease 2s forwards,
    sweep 2.5s infinite cubic-bezier(0.4, 0, 0.2, 1) 1s,
    rock 4s infinite ease-in-out 1s,
    pulse 1.5s infinite ease-in-out 1s,
    cycleColor 6s infinite linear 1s;
  border-radius: 2px;
  opacity: 0.9;
  z-index: 2;
}

@keyframes fadeIn {
  to {
    top: 0;
    opacity: 0.6;
  }
}

@keyframes sweep {
  0% {
    top: 0%;
  }
  50% {
    top: 100%;
  }
  100% {
    top: 0%;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.2;
    box-shadow:
      0 0 8px 1px var(--laser-color, var(--brand-primary-color)),
      0 0 12px 1px var(--laser-color, var(--brand-primary-color));
  }
  50% {
    opacity: 0.6;
    box-shadow:
      0 0 16px 4px var(--laser-color, var(--brand-primary-color)),
      0 0 32px 4px var(--laser-color, var(--brand-primary-color));
  }
}

@keyframes cycleColor {
  0% {
    --laser-color: var(--brand-primary-color);
  }
  33% {
    --laser-color: var(--warning-color);
  }
  66% {
    --laser-color: var(--success-color);
  }
  100% {
    --laser-color: var(--brand-primary-color);
  }
}

@keyframes rock {
  0% {
    transform: translateX(-50%) rotate(-10deg);
  }
  50% {
    transform: translateX(-50%) rotate(10deg);
  }
  100% {
    transform: translateX(-50%) rotate(-10deg);
  }
}

/* Corner markers */
.corner {
  position: absolute;
  width: 2rem;
  height: 2rem;
  border: 4px solid rgba(255, 255, 255, 0.9);
  z-index: 3;
}

.corner.top-left {
  top: 0;
  left: 0;
  border-right: none;
  border-bottom: none;
  border-top-left-radius: 16px;
}

.corner.top-right {
  top: 0;
  right: 0;
  border-left: none;
  border-bottom: none;
  border-top-right-radius: 16px;
}

.corner.bottom-left {
  bottom: 0;
  left: 0;
  border-right: none;
  border-top: none;
  border-bottom-left-radius: 16px;
}

.corner.bottom-right {
  bottom: 0;
  right: 0;
  border-left: none;
  border-top: none;
  border-bottom-right-radius: 16px;
}

.qr-instruction {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-medium-large);
  padding: var(--sizing-4);
  color: var(--ui-grey-400);
}
</style>
